import { useApplicantAPI } from "@certn/api";
import {
  BasicButton,
  InnerContentItem,
  Link,
  SummaryCard,
  SummaryCardSkipSection,
  SummaryMessage,
  Text,
} from "@certn/cascadia-react";
import { Skeleton } from "@certn/cascadia-react/patterns/skeleton/Skeleton";
import { formatCapitalizedString } from "@certn/utils";
import { css } from "@emotion/css";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { useApplicationFormContext } from "../../ApplicationFormContext";
import { useContext } from "react";
import { ApplicationContext } from "../../ApplicationContext";
import { checkIsString, parseStringRecord } from "../../utils";

type IvSummaryProps = {
  otherSkipReason: string | undefined;
};

export function IncomeVerificationSummary(props: IvSummaryProps) {
  const { t } = useTranslation();
  const { caseId } = useApplicationFormContext();
  const { basePath = "apply" } = useContext(ApplicationContext);
  const navigate = useNavigate();
  const { data, mutate } = useApplicantAPI(
    `/api/internal/applicant/case/${caseId}/` as "/api/internal/applicant/case/{id}/"
  );

  const countryPresentThereforeApplicantCompleted = checkIsString(
    data?.applicant_form_data?.income_verification_country
  );
  const incomeVerificationSkipDetails = parseStringRecord(
    data?.applicant_form_data?.income_verification_skip
  );
  const additionalInformation = checkIsString(
    data?.applicant_form_data?.income_verification_additional_information
  );
  const skipReason = formatCapitalizedString(
    checkIsString(incomeVerificationSkipDetails?.skip_reason)
  );

  useEffect(() => {
    const interval = setInterval(() => {
      mutate();
      if (data?.applicant_form_data?.flinks_completed) {
        clearInterval(interval);
      }
    }, 3000);
    return () => clearInterval(interval);
  }, [data?.applicant_form_data?.flinks_completed, mutate]);

  if (data?.applicant_form_data?.flinks_completed) {
    return (
      <SummaryCard
        header={t("Income Verification")}
        testID="income-verification-summary-card"
        actions={
          <Link
            href={`/${basePath}/${caseId}/income-verification`}
            textColor="text-weak"
            testID="income-verification-edit-button"
          >
            {t("Edit")}
          </Link>
        }
      >
        <InnerContentItem label={t("Connected account")}>
          {t("Yes")}
        </InnerContentItem>
        <InnerContentItem label={t("Additional information")}>
          {additionalInformation ? additionalInformation : t("None provided")}
        </InnerContentItem>
      </SummaryCard>
    );
  }

  if (incomeVerificationSkipDetails?.skip_verification) {
    if (props.otherSkipReason) {
      skipReason?.concat(" - ", props.otherSkipReason);
    }

    return (
      <SummaryCardSkipSection
        title={t("Income Verification skipped")}
        sectionTitle={t("Income Verification")}
        testID="income-verification-summary-card"
        sectionEditUrl={`/${basePath}/${caseId}/income-verification`}
        reason={skipReason}
      />
    );
  }

  if (countryPresentThereforeApplicantCompleted) {
    return (
      <SummaryCard
        header={t("Income Verification")}
        testID="income-verification-summary-card"
      >
        <Skeleton className={styles.skeletonPadding} height="100px" />
      </SummaryCard>
    );
  }

  return (
    <SummaryCard
      header={t("Income Verification")}
      testID="income-verification-summary-card"
    >
      <SummaryMessage type="danger">
        <Text marginTop="space-05" style="global/heading-small">
          {t("Incomplete step")}
        </Text>
      </SummaryMessage>

      <div className={styles.incompleteContainer}>
        {t("This step has not been completed yet.")}
        <BasicButton
          size="medium"
          href={`/${basePath}/${caseId}/income-verification`}
          testID="summary-income-verification-section-complete-this-step-button"
          onPress={() => {
            navigate(`/${basePath}/${caseId}/income-verification`);
          }}
        >
          {t("Complete this step")}
        </BasicButton>
      </div>
    </SummaryCard>
  );
}

const styles = {
  skippedContainer: css({
    padding: "var(--space-4)",
    display: "flex",
    flexDirection: "row",
  }),
  skippedIcon: css({
    paddingRight: "var(--space-2)",
  }),
  skippedTextContainer: css({ display: "flex", flexDirection: "column" }),
  skippedTextPadding: css({ paddingBottom: "var(--space-1)" }),
  incompleteContainer: css({
    display: "flex",
    flexDirection: "column",
    gap: "var(--space-3)",
    padding: "var(--space-4)",
  }),
  skeletonPadding: css({
    padding: "var(--space-2)",
  }),
};
